import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Search, Eye, Ban, CheckCircle, Trash2, Edit, GitMerge,
  Users, Briefcase, Building2, Shield, UserPlus, Loader2,
  ChevronDown, ChevronRight, Link2, Crown, Radio,
  Snowflake, AlertTriangle, Clock, Eraser, Sliders, X, Wrench, Database, LogIn,
} from "lucide-react";
import { Progress } from "../../components/ui/progress";
import { API } from "../../App";
import { toast } from "sonner";
import { format } from "date-fns";
import MergeAccountsDialog from "./MergeAccountsDialog";
import { startImpersonation } from "../../utils/impersonation";

/* ── helpers ── */
const getStatusBadge = (status) => {
  if (status === "active") return <Badge data-testid="status-active" className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25 text-[11px] font-semibold">Active</Badge>;
  if (status === "suspended") return <Badge data-testid="status-suspended" className="bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/25 text-[11px] font-semibold">Suspended</Badge>;
  if (status === "trialing") return <Badge data-testid="status-trialing" className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/25 text-[11px] font-semibold">Trialing</Badge>;
  if (status === "frozen") return <Badge data-testid="status-frozen" className="bg-sky-500/15 text-sky-600 dark:text-sky-300 border border-sky-500/25 text-[11px] font-semibold flex items-center gap-1"><Snowflake className="w-3 h-3" />Frozen</Badge>;
  if (status === "cancelled") return <Badge data-testid="status-cancelled" className="bg-slate-500/15 text-slate-600 dark:text-slate-400 border border-slate-500/25 text-[11px] font-semibold">Cancelled</Badge>;
  return <Badge className="bg-muted text-muted-foreground text-[11px]">{status || "—"}</Badge>;
};

const TrialExpiredBadge = () => (
  <Badge data-testid="badge-trial-expired" className="bg-orange-500/15 text-orange-600 dark:text-orange-400 border border-orange-500/25 text-[10px] font-bold tracking-wide flex items-center gap-1">
    <AlertTriangle className="w-3 h-3" />TRIAL EXPIRED
  </Badge>
);

const getPlanBadge = (planId, plans) => {
  const colors = {
    starter: "bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/25",
    standard: "bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/25",
    pro: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/25",
    premium: "bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/25",
    enterprise: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/25",
  };
  const plan = plans.find(p => p.plan_id === planId);
  const name = plan?.name || planId || "—";
  const cls = colors[planId] || "bg-muted text-muted-foreground border-transparent";
  return <Badge className={`${cls} border text-[11px] font-semibold`}>{name}</Badge>;
};

const getRoleBadge = (user) => {
  if (user.is_admin) return <Badge className="bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/25 text-[10px] font-bold tracking-wide">SUPER ADMIN</Badge>;
  if (user.parent_user_id && user.role === "admin") return <Badge className="bg-purple-500/15 text-purple-600 dark:text-purple-400 border border-purple-500/25 text-[10px] font-bold tracking-wide">INVITED ADMIN</Badge>;
  if (user.role === "dispatcher") return <Badge className="bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 border border-cyan-500/25 text-[10px] font-bold tracking-wide">DISPATCHER</Badge>;
  if (user.parent_user_id) return <Badge className="bg-teal-500/15 text-teal-600 dark:text-teal-400 border border-teal-500/25 text-[10px] font-bold tracking-wide">SUB-ACCOUNT</Badge>;
  return null;
};

/* ── stat pill ── */
const StatPill = ({ icon: Icon, value, label, color }) => (
  <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full ${color} text-xs font-medium`}>
    <Icon className="w-3 h-3" />{value}<span className="hidden sm:inline text-[10px] opacity-70">{label}</span>
  </div>
);

/* ── action buttons ── */
const ActionButtons = ({ user, onView, onEdit, onSuspend, onActivate, onDelete, onMerge, onImpersonate }) => (
  <div className="flex items-center gap-0.5" data-testid={`user-actions-${user.user_id}`}>
    <Button variant="ghost" size="icon" onClick={() => onView(user)} className="h-7 w-7 text-muted-foreground hover:text-foreground" data-testid={`view-user-${user.user_id}`}><Eye className="w-3.5 h-3.5" /></Button>
    {!user.is_admin && (
      <Button variant="ghost" size="icon" onClick={() => onImpersonate(user)} className="h-7 w-7 text-amber-500 hover:text-amber-400" title="Log in as customer" data-testid={`impersonate-user-${user.user_id}`}><LogIn className="w-3.5 h-3.5" /></Button>
    )}
    <Button variant="ghost" size="icon" onClick={() => onEdit(user)} className="h-7 w-7 text-blue-500 hover:text-blue-400" data-testid={`edit-user-${user.user_id}`}><Edit className="w-3.5 h-3.5" /></Button>
    {!user.parent_user_id && !user.is_admin && user.account_status !== "merged" && (
      <Button variant="ghost" size="icon" onClick={() => onMerge(user)} className="h-7 w-7 text-purple-500 hover:text-purple-400" title="Merge into another account" data-testid={`merge-user-${user.user_id}`}><GitMerge className="w-3.5 h-3.5" /></Button>
    )}
    {user.subscription_status === "active" ? (
      <Button variant="ghost" size="icon" onClick={() => onSuspend(user.user_id)} className="h-7 w-7 text-orange-500 hover:text-orange-400" data-testid={`suspend-user-${user.user_id}`}><Ban className="w-3.5 h-3.5" /></Button>
    ) : (
      <Button variant="ghost" size="icon" onClick={() => onActivate(user.user_id)} className="h-7 w-7 text-emerald-500 hover:text-emerald-400" data-testid={`activate-user-${user.user_id}`}><CheckCircle className="w-3.5 h-3.5" /></Button>
    )}
    <Button variant="ghost" size="icon" onClick={() => onDelete(user.user_id)} className="h-7 w-7 text-red-500 hover:text-red-400" data-testid={`delete-user-${user.user_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
  </div>
);

/* ── Custom Limits override section (per-user, inside the Edit User dialog)
 * --------------------------------------------------------------------
 * Lets a super admin override individual limits on top of the user's plan
 * without creating a separate custom Stripe plan.
 *
 * Each row shows the plan's current value as the input placeholder. Empty
 * input → inherit from plan. Numeric `-1` → unlimited (same convention as
 * subscription_plans). The "✕" button on each row clears the override for
 * that field. Booleans default to plan value when missing; the inline
 * Select lets you force them on/off or "Use plan default".
 *
 * Persisted to user.plan_overrides via PUT /api/admin/users/{id}.
 * --------------------------------------------------------------------*/
const NUMERIC_LIMITS = [
  { key: "sms_limit",       label: "SMS / month",      hint: "-1 = unlimited" },
  { key: "minutes_limit",   label: "Voice minutes / month", hint: "-1 = unlimited" },
  { key: "max_users",       label: "Max team users",   hint: "-1 = unlimited" },
  { key: "dedicated_lines", label: "Dedicated phone lines", hint: "-1 = unlimited" },
  { key: "max_locations",   label: "Max store locations",   hint: "-1 = unlimited" },
];

const BOOL_LIMITS = [
  { key: "unlimited_technicians",  label: "Unlimited technicians" },
  { key: "call_masking",           label: "Call masking" },
  { key: "joby_automation",        label: "FIELDZ automation" },
  { key: "quickbooks_integration", label: "QuickBooks integration" },
  { key: "customer_portal",        label: "Customer portal" },
  { key: "inventory_management",   label: "Inventory management" },
];

const formatNum = (n) => (n === -1 ? "unlimited" : (n == null ? "—" : String(n)));

const CustomLimitsSection = ({ plan, overrides, setOverrides }) => {
  const [open, setOpen] = useState(false);

  const clearKey = (k) => {
    const next = { ...overrides };
    delete next[k];
    setOverrides(next);
  };

  const setNumeric = (k, raw) => {
    if (raw === "" || raw == null) { clearKey(k); return; }
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return;
    setOverrides({ ...overrides, [k]: n });
  };

  const setBool = (k, val) => {
    // val is "inherit" | "on" | "off"
    if (val === "inherit") { clearKey(k); return; }
    setOverrides({ ...overrides, [k]: val === "on" });
  };

  const overrideCount = Object.keys(overrides || {}).length;

  return (
    <div className="border border-theme/60 rounded-lg overflow-hidden" data-testid="custom-limits-section">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-theme-surface/40 hover:bg-theme-surface transition-colors"
        data-testid="custom-limits-toggle"
      >
        <div className="flex items-center gap-2">
          <Sliders className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-semibold text-foreground">Custom Limits</span>
          {overrideCount > 0 && (
            <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/25 text-[10px] font-bold">
              {overrideCount} override{overrideCount === 1 ? "" : "s"}
            </Badge>
          )}
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="p-3 space-y-3 border-t border-theme/60">
          <p className="text-[11px] text-muted-foreground">
            Empty field = inherit from {plan?.name || "plan"}. Enter <code className="text-amber-500">-1</code> for unlimited.
            These overrides apply only to this user.
          </p>

          {/* Numeric overrides */}
          <div className="space-y-2">
            {NUMERIC_LIMITS.map(({ key, label, hint }) => {
              const has = overrides[key] !== undefined && overrides[key] !== null && overrides[key] !== "";
              const planVal = plan?.[key];
              return (
                <div key={key} className="flex items-center gap-2" data-testid={`limit-row-${key}`}>
                  <label className="text-xs text-muted-foreground flex-1 min-w-0 truncate">
                    {label} <span className="opacity-50">(plan: {formatNum(planVal)})</span>
                  </label>
                  <Input
                    type="number"
                    min="-1"
                    value={has ? overrides[key] : ""}
                    placeholder={planVal != null ? String(planVal) : "—"}
                    onChange={(e) => setNumeric(key, e.target.value)}
                    className="bg-theme-surface border-theme text-foreground h-8 w-28 text-sm"
                    data-testid={`limit-input-${key}`}
                    title={hint}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => clearKey(key)}
                    disabled={!has}
                    className="h-8 w-8 text-muted-foreground hover:text-foreground disabled:opacity-30"
                    data-testid={`limit-clear-${key}`}
                    title="Reset to plan default"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Boolean overrides */}
          <div className="pt-1 border-t border-theme/40 space-y-2">
            {BOOL_LIMITS.map(({ key, label }) => {
              const has = overrides[key] !== undefined;
              const planVal = plan?.[key];
              const current = has ? (overrides[key] ? "on" : "off") : "inherit";
              return (
                <div key={key} className="flex items-center gap-2" data-testid={`bool-row-${key}`}>
                  <label className="text-xs text-muted-foreground flex-1 min-w-0 truncate">
                    {label} <span className="opacity-50">(plan: {planVal ? "on" : "off"})</span>
                  </label>
                  <Select value={current} onValueChange={(v) => setBool(key, v)}>
                    <SelectTrigger className="bg-theme-surface border-theme text-foreground h-8 w-28 text-xs" data-testid={`limit-bool-${key}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-theme">
                      <SelectItem value="inherit">Inherit</SelectItem>
                      <SelectItem value="on">On</SelectItem>
                      <SelectItem value="off">Off</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="w-8" />
                </div>
              );
            })}
          </div>

          {overrideCount > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setOverrides({})}
              className="w-full h-8 text-xs border-theme/60 text-muted-foreground hover:text-foreground"
              data-testid="limits-clear-all"
            >
              <X className="w-3.5 h-3.5 mr-1.5" /> Reset all to plan defaults
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

/* ── dispatcher sub-row ── */
const DispatcherRow = ({ user, plans, actions }) => (
  <div
    className="flex items-center gap-3 py-2.5 px-4 ml-8 border-l-2 border-cyan-500/30 bg-cyan-500/[0.03] rounded-r-lg hover:bg-cyan-500/[0.06] transition-colors"
    data-testid={`dispatcher-row-${user.user_id}`}
  >
    <Link2 className="w-3.5 h-3.5 text-cyan-500/60 shrink-0" />
    <div className="flex-1 min-w-0 flex items-center gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground truncate">{user.name}</span>
          {getRoleBadge(user)}
        </div>
        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
      </div>
    </div>
    <div className="hidden md:flex items-center gap-2">
      {getStatusBadge(user.subscription_status)}
    </div>
    <div className="hidden lg:block text-xs text-muted-foreground">
      {user.created_at ? format(new Date(user.created_at), "MMM d, yyyy") : "—"}
    </div>
    <ActionButtons user={user} {...actions} />
  </div>
);

/* ── owner card with nested dispatchers ── */
const OwnerCard = ({ user, dispatchers, plans, actions, expanded, onToggle }) => {
  const hasDispatchers = dispatchers.length > 0;

  return (
    <div className="border border-border/60 rounded-xl overflow-hidden bg-card/50 hover:border-border transition-colors" data-testid={`owner-card-${user.user_id}`}>
      {/* main owner row */}
      <div className="flex items-center gap-3 p-4">
        {/* expand toggle */}
        <button
          onClick={() => hasDispatchers && onToggle(user.user_id)}
          className={`shrink-0 w-6 h-6 flex items-center justify-center rounded-md transition-colors ${hasDispatchers ? "hover:bg-muted cursor-pointer text-muted-foreground" : "text-transparent cursor-default"}`}
          data-testid={`toggle-dispatchers-${user.user_id}`}
        >
          {hasDispatchers && (expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)}
        </button>

        {/* avatar */}
        <div className="shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-[#0046BE] to-[#0046BE]/60 flex items-center justify-center text-white text-sm font-bold">
          {user.name?.charAt(0)?.toUpperCase() || "?"}
        </div>

        {/* user info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-foreground truncate">{user.name}</span>
            {user.is_admin && <Shield className="w-3.5 h-3.5 text-red-500" />}
            {getRoleBadge(user)}
            {user.trial_expired && <TrialExpiredBadge />}
            {hasDispatchers && (
              <Badge className="bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border border-cyan-500/20 text-[10px]">
                {dispatchers.length} sub-account{dispatchers.length > 1 ? "s" : ""}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-muted-foreground truncate">{user.email}</span>
            {user.company_name && (
              <>
                <span className="text-muted-foreground/40">|</span>
                <span className="text-xs text-muted-foreground/80 flex items-center gap-1 truncate">
                  <Building2 className="w-3 h-3" />{user.company_name}
                </span>
              </>
            )}
          </div>
        </div>

        {/* stats pills */}
        <div className="hidden lg:flex items-center gap-2">
          <StatPill icon={Briefcase} value={user.total_jobs || 0} label="jobs" color="bg-blue-500/10 text-blue-600 dark:text-blue-400" />
          <StatPill icon={Users} value={user.total_customers || 0} label="cust" color="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" />
        </div>

        {/* plan + status */}
        <div className="hidden md:flex items-center gap-2">
          {getPlanBadge(user.subscription_plan, plans)}
          {getStatusBadge(user.subscription_status)}
        </div>

        {/* joined */}
        <div className="hidden xl:block text-xs text-muted-foreground whitespace-nowrap">
          {user.created_at ? format(new Date(user.created_at), "MMM d, yyyy") : "—"}
        </div>

        {/* actions */}
        <ActionButtons user={user} {...actions} />
      </div>

      {/* dispatcher sub-rows */}
      {hasDispatchers && expanded && (
        <div className="border-t border-border/40 bg-muted/20 py-1.5 space-y-0.5 px-4" data-testid={`dispatchers-list-${user.user_id}`}>
          {dispatchers.map(d => (
            <DispatcherRow key={d.user_id} user={d} plans={plans} actions={actions} />
          ))}
        </div>
      )}
    </div>
  );
};

/* ── orphan dispatcher card (parent not in current page) ── */
const OrphanDispatcherCard = ({ user, plans, actions }) => (
  <div className="border border-cyan-500/30 rounded-xl overflow-hidden bg-cyan-500/[0.02]" data-testid={`orphan-dispatcher-${user.user_id}`}>
    <div className="flex items-center gap-3 p-4">
      <div className="shrink-0 w-6 h-6 flex items-center justify-center">
        <Radio className="w-4 h-4 text-cyan-500/50" />
      </div>
      <div className="shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center text-white text-sm font-bold">
        {user.name?.charAt(0)?.toUpperCase() || "?"}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm text-foreground truncate">{user.name}</span>
          {getRoleBadge(user)}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-muted-foreground truncate">{user.email}</span>
          {user.parent_info && (
            <>
              <span className="text-muted-foreground/40">|</span>
              <span className="text-[11px] text-cyan-600 dark:text-cyan-400 flex items-center gap-1">
                <Crown className="w-3 h-3" />
                {user.parent_info.name} ({user.parent_info.email})
              </span>
            </>
          )}
        </div>
      </div>
      <div className="hidden md:flex items-center gap-2">
        {getStatusBadge(user.subscription_status)}
      </div>
      <div className="hidden xl:block text-xs text-muted-foreground whitespace-nowrap">
        {user.created_at ? format(new Date(user.created_at), "MMM d, yyyy") : "—"}
      </div>
      <ActionButtons user={user} {...actions} />
    </div>
  </div>
);


/* ══════════════════════ MAIN COMPONENT ══════════════════════ */
const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSource, setMergeSource] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [editData, setEditData] = useState({});
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [expandedOwners, setExpandedOwners] = useState(new Set());
  const [createData, setCreateData] = useState({
    name: "", email: "", password: "", company_name: "",
    subscription_plan: "starter", subscription_status: "active", is_admin: false,
  });
  // Clear-Jobs-Data confirmation dialog state. Keeps the destructive action
  // one extra click away from the passive "View User" panel so it can't be
  // triggered by accident.
  const [clearJobsDialogOpen, setClearJobsDialogOpen] = useState(false);
  const [repairDialogOpen, setRepairDialogOpen] = useState(false);
  const [repairPreview, setRepairPreview] = useState(null);
  const [repairLoading, setRepairLoading] = useState(false);
  const [repairApplying, setRepairApplying] = useState(false);
  // Repair Job Data (dates + numeric normalization) dialog state
  const [repairDataDialogOpen, setRepairDataDialogOpen] = useState(false);
  const [repairDataRunning, setRepairDataRunning] = useState(false);
  const [repairDataProgress, setRepairDataProgress] = useState(null);
  const [repairDataResult, setRepairDataResult] = useState(null);
  const [clearingJobs, setClearingJobs] = useState(false);
  const [clearCustomersDialogOpen, setClearCustomersDialogOpen] = useState(false);
  const [clearingCustomers, setClearingCustomers] = useState(false);

  const getAuthConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await axios.get(`${API}/admin/subscription-plans`, getAuthConfig());
        setSubscriptionPlans(res.data || []);
      } catch (error) {
        console.error("Failed to fetch subscription plans:", error);
      }
    };
    fetchPlans();
  }, []);

  useEffect(() => { fetchUsers(); }, [search, planFilter, statusFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (planFilter !== "all") params.append("plan", planFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);
      const res = await axios.get(`${API}/admin/users?${params.toString()}`, getAuthConfig());
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (error) {
      if (error.response?.status !== 401) toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const viewUser = async (user) => {
    setSelectedUser(user);
    setViewDialogOpen(true);
    try {
      const res = await axios.get(`${API}/admin/users/${user.user_id}`, getAuthConfig());
      setUserDetails(res.data);
    } catch { toast.error("Failed to load user details"); }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditData({
      name: user.name, email: user.email, company_name: user.company_name || "",
      subscription_plan: user.subscription_plan || "starter",
      subscription_status: user.subscription_status || "active",
      is_admin: user.is_admin || false,
      is_internal: user.is_internal || false,
      account_type: user.account_type || null,
      // plan_overrides is a dict like {sms_limit: 5000, call_masking: true}.
      // Missing/empty keys inherit from the plan; -1 on a numeric field
      // means "unlimited" (same convention as plans).
      plan_overrides: user.plan_overrides || {},
    });
    setEditDialogOpen(true);
  };

  const saveUser = async () => {
    try {
      await axios.put(`${API}/admin/users/${selectedUser.user_id}`, editData, getAuthConfig());
      toast.success("User updated successfully");
      setEditDialogOpen(false);
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to update user"); }
  };

  const suspendUser = async (userId) => {
    if (!window.confirm("Are you sure you want to suspend this user?")) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/suspend`, {}, getAuthConfig());
      toast.success("User suspended");
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to suspend user"); }
  };

  const activateUser = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/activate`, {}, getAuthConfig());
      toast.success("User activated");
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to activate user"); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to DELETE this user and ALL their data? This cannot be undone!")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, getAuthConfig());
      toast.success("User deleted");
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to delete user"); }
  };

  const openRepairDialog = async () => {
    if (!selectedUser?.user_id) return;
    setRepairDialogOpen(true);
    setRepairPreview(null);
    setRepairLoading(true);
    try {
      const res = await axios.post(
        `${API}/admin/users/${selectedUser.user_id}/repair-commissions/preview`, {}, getAuthConfig()
      );
      setRepairPreview(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to scan jobs");
      setRepairDialogOpen(false);
    } finally {
      setRepairLoading(false);
    }
  };

  const applyRepair = async () => {
    if (!selectedUser?.user_id) return;
    setRepairApplying(true);
    try {
      const res = await axios.post(
        `${API}/admin/users/${selectedUser.user_id}/repair-commissions/apply`, {}, getAuthConfig()
      );
      toast.success(`Repaired ${res.data.jobs_updated} job${res.data.jobs_updated === 1 ? "" : "s"} — commissions now match each technician's config`, { duration: 6000 });
      setRepairDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Repair failed");
    } finally {
      setRepairApplying(false);
    }
  };

  // Repair Job Data — normalizes date fields (ISO or None) and forces
  // price / duration_hours numeric on every job of this account.
  const runRepairJobData = async () => {
    if (!selectedUser?.user_id) return;
    setRepairDataRunning(true);
    setRepairDataResult(null);
    setRepairDataProgress({ processed: 0, total: 0 });
    const uid = selectedUser.user_id;
    const poll = setInterval(async () => {
      try {
        const p = await axios.get(`${API}/admin/users/${uid}/repair-job-data/progress`, getAuthConfig());
        if (p.data?.status === "running") setRepairDataProgress(p.data);
      } catch { /* keep polling */ }
    }, 600);
    try {
      const res = await axios.post(`${API}/admin/users/${uid}/repair-job-data`, {}, getAuthConfig());
      setRepairDataResult(res.data);
      setRepairDataProgress({ processed: res.data.scanned, total: res.data.scanned });
      toast.success(
        res.data.jobs_updated > 0
          ? `Cleaned ${res.data.jobs_updated} of ${res.data.scanned} jobs`
          : `All ${res.data.scanned} jobs were already clean`,
        { duration: 6000 }
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Data repair failed");
    } finally {
      clearInterval(poll);
      setRepairDataRunning(false);
    }
  };

  // Hits DELETE /api/admin/users/{id}/jobs — scoped hard-delete that wipes
  // ONLY the `db.jobs` collection for this account. Customers, invoices,
  // settings, and team data are untouched. Audit row is written server-side.
  const clearJobsForSelectedUser = async () => {
    if (!selectedUser?.user_id) return;
    setClearingJobs(true);
    try {
      const res = await axios.delete(
        `${API}/admin/users/${selectedUser.user_id}/jobs`,
        getAuthConfig(),
      );
      const { deleted_count = 0, account_name = selectedUser.name } = res.data || {};
      toast.success(
        `Cleared ${deleted_count} job${deleted_count === 1 ? "" : "s"} for ${account_name}`,
        { duration: 5000 },
      );
      setClearJobsDialogOpen(false);
      // Refresh the detail panel so the Total Jobs stat reflects the delete.
      if (selectedUser?.user_id) {
        try {
          const det = await axios.get(`${API}/admin/users/${selectedUser.user_id}`, getAuthConfig());
          setUserDetails(det.data);
        } catch { /* silent — the toast already confirms success */ }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear jobs");
    } finally {
      setClearingJobs(false);
    }
  };

  // Hits DELETE /api/admin/users/{id}/customers — scoped hard-delete that
  // wipes ONLY the `db.customers` collection for this account. Jobs,
  // invoices, settings, and team data are untouched. Audit row is server-side.
  const clearCustomersForSelectedUser = async () => {
    if (!selectedUser?.user_id) return;
    setClearingCustomers(true);
    try {
      const res = await axios.delete(
        `${API}/admin/users/${selectedUser.user_id}/customers`,
        getAuthConfig(),
      );
      const { deleted_count = 0, account_name = selectedUser.name } = res.data || {};
      toast.success(
        `Cleared ${deleted_count} customer${deleted_count === 1 ? "" : "s"} for ${account_name}`,
        { duration: 5000 },
      );
      setClearCustomersDialogOpen(false);
      if (selectedUser?.user_id) {
        try {
          const det = await axios.get(`${API}/admin/users/${selectedUser.user_id}`, getAuthConfig());
          setUserDetails(det.data);
        } catch { /* silent — the toast already confirms success */ }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear customers");
    } finally {
      setClearingCustomers(false);
    }
  };

  const createUser = async () => {
    if (!createData.name || !createData.email || !createData.password) {
      toast.error("Name, email, and password are required");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/users`, createData, getAuthConfig());
      toast.success("User created successfully");
      setCreateDialogOpen(false);
      setCreateData({ name: "", email: "", password: "", company_name: "", subscription_plan: "starter", subscription_status: "active", is_admin: false });
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to create user"); }
    finally { setCreating(false); }
  };

  const toggleExpand = (ownerId) => {
    setExpandedOwners(prev => {
      const next = new Set(prev);
      next.has(ownerId) ? next.delete(ownerId) : next.add(ownerId);
      return next;
    });
  };

  /* ── group users: owners (no parent) with their sub-accounts nested ── */
  const owners = users.filter(u => !u.parent_user_id);
  const subAccounts = users.filter(u => u.parent_user_id);
  const subAccountsByParent = {};
  const orphanSubAccounts = [];
  subAccounts.forEach(s => {
    const pid = s.parent_user_id;
    const parentInList = pid && owners.find(o => o.user_id === pid);
    if (parentInList) {
      if (!subAccountsByParent[pid]) subAccountsByParent[pid] = [];
      subAccountsByParent[pid].push(s);
    } else {
      orphanSubAccounts.push(s);
    }
  });

  const ownerCount = owners.length;
  const subAccountCount = subAccounts.length;
  const frozenCount = users.filter(u => u.subscription_status === "frozen").length;
  const trialExpiredCount = users.filter(u => u.trial_expired).length;

  const impersonateUser = async (u) => {
    if (!window.confirm(`Log in as ${u.name || u.email}? Your admin session stays active and this access is recorded in the Support Access Log.`)) return;
    try {
      await startImpersonation(u);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start support session");
    }
  };

  const actions = { onView: viewUser, onEdit: openEditDialog, onSuspend: suspendUser, onActivate: activateUser, onDelete: deleteUser, onMerge: (u) => { setMergeSource(u); setMergeOpen(true); }, onImpersonate: impersonateUser };

  return (
    <div className="space-y-5" data-testid="admin-users-page">
      {/* header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Manrope']">User Management</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            {total} total &mdash; {ownerCount} owner{ownerCount !== 1 ? "s" : ""}, {subAccountCount} sub-account{subAccountCount !== 1 ? "s" : ""}
            {frozenCount > 0 && <span className="text-sky-500"> &middot; {frozenCount} frozen</span>}
            {trialExpiredCount > 0 && <span className="text-orange-500"> &middot; {trialExpiredCount} trial expired</span>}
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} className="bg-[#0046BE] text-white hover:bg-[#0046BE]/80" data-testid="create-user-btn">
          <UserPlus className="w-4 h-4 mr-2" />Create User
        </Button>
      </div>

      {/* filters */}
      <Card className="glass-card border-theme">
        <CardContent className="p-3">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, company..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-theme-surface border-theme text-foreground h-9"
                  data-testid="search-users"
                />
              </div>
            </div>
            <Select value={planFilter} onValueChange={setPlanFilter}>
              <SelectTrigger className="w-[140px] bg-theme-surface border-theme text-foreground h-9" data-testid="filter-plan">
                <SelectValue placeholder="Plan" />
              </SelectTrigger>
              <SelectContent className="bg-popover border-theme">
                <SelectItem value="all">All Plans</SelectItem>
                {subscriptionPlans.map(plan => (
                  <SelectItem key={plan.plan_id} value={plan.plan_id}>{plan.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px] bg-theme-surface border-theme text-foreground h-9" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-popover border-theme">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="trialing">Trialing</SelectItem>
                <SelectItem value="trial_expired">Trial Expired</SelectItem>
                <SelectItem value="frozen">Frozen</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* user list */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />Loading users...
        </div>
      ) : users.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <Users className="w-10 h-10 mb-3 opacity-40" />
          <p className="text-sm">No users found</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="users-list">
          {owners.map(owner => (
            <OwnerCard
              key={owner.user_id}
              user={owner}
              dispatchers={subAccountsByParent[owner.user_id] || []}
              plans={subscriptionPlans}
              actions={actions}
              expanded={expandedOwners.has(owner.user_id)}
              onToggle={toggleExpand}
            />
          ))}
          {orphanSubAccounts.length > 0 && (
            <>
              <div className="pt-3 pb-1 px-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Sub-accounts (parent not on this page)</p>
              </div>
              {orphanSubAccounts.map(d => (
                <OrphanDispatcherCard key={d.user_id} user={d} plans={subscriptionPlans} actions={actions} />
              ))}
            </>
          )}
        </div>
      )}

      {/* View User Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-background border-theme text-foreground max-w-2xl" data-testid="view-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground font-['Manrope']">User Details</DialogTitle>
          </DialogHeader>
          {userDetails && (
            <div className="space-y-4">
              {!userDetails.user.is_admin && (
                <Button
                  size="sm"
                  onClick={() => impersonateUser(userDetails.user)}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                  data-testid="impersonate-from-details-btn"
                >
                  <LogIn className="w-4 h-4 mr-1.5" />
                  Log in as customer
                </Button>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-muted-foreground text-xs mb-0.5">Name</p>
                  <p className="text-foreground font-medium text-sm">{userDetails.user.name}</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-muted-foreground text-xs mb-0.5">Email</p>
                  <p className="text-foreground text-sm">{userDetails.user.email}</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-muted-foreground text-xs mb-0.5">Company</p>
                  <p className="text-foreground text-sm">{userDetails.user.company_name || "—"}</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-muted-foreground text-xs mb-0.5">Plan</p>
                  {getPlanBadge(userDetails.user.subscription_plan, subscriptionPlans)}
                </div>
                {userDetails.user.parent_user_id && (
                  <div className="col-span-2 bg-cyan-500/10 rounded-lg p-3 border border-cyan-500/20">
                    <p className="text-cyan-600 dark:text-cyan-400 text-xs mb-0.5 font-semibold">Parent Account</p>
                    <p className="text-foreground text-sm">{userDetails.user.parent_user_id}</p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-blue-500/10 rounded-lg p-3 text-center">
                  <Briefcase className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                  <p className="text-xl font-bold text-blue-400">{userDetails.stats.total_jobs}</p>
                  <p className="text-muted-foreground text-[11px]">Total Jobs</p>
                </div>
                <div className="bg-emerald-500/10 rounded-lg p-3 text-center">
                  <Users className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                  <p className="text-xl font-bold text-emerald-400">{userDetails.stats.total_customers}</p>
                  <p className="text-muted-foreground text-[11px]">Customers</p>
                </div>
                <div className="bg-[#FFCC00]/10 rounded-lg p-3 text-center">
                  <Building2 className="w-5 h-5 text-[#FFCC00] mx-auto mb-1" />
                  <p className="text-xl font-bold text-[#FFCC00]">${userDetails.stats.total_revenue?.toLocaleString() || 0}</p>
                  <p className="text-muted-foreground text-[11px]">Revenue</p>
                </div>
              </div>

              {/* Maintenance — safe data-repair tools for this account */}
              <div className="mt-2 rounded-lg border border-cyan-500/25 bg-cyan-500/[0.04] p-3 space-y-2.5" data-testid="user-maintenance-zone">
                <div className="flex items-start gap-2.5">
                  <Wrench className="w-4 h-4 text-cyan-500 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-cyan-600 dark:text-cyan-400">Maintenance</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Repair Tech Commissions rewrites every job's commission rate (including settled jobs) to match
                      each technician's current commission config. Repair Data normalizes broken job dates
                      (ISO or empty) and forces price / duration to numbers.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pl-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openRepairDialog}
                    className="border-cyan-500/40 text-cyan-600 hover:text-cyan-700 hover:bg-cyan-500/10 shrink-0"
                    data-testid="repair-commissions-btn"
                  >
                    <Wrench className="w-3.5 h-3.5 mr-1.5" />
                    Repair Tech Commissions
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setRepairDataResult(null); setRepairDataProgress(null); setRepairDataDialogOpen(true); }}
                    className="border-cyan-500/40 text-cyan-600 hover:text-cyan-700 hover:bg-cyan-500/10 shrink-0"
                    data-testid="repair-job-data-btn"
                  >
                    <Database className="w-3.5 h-3.5 mr-1.5" />
                    Repair Data
                  </Button>
                </div>
              </div>

              {/* Danger Zone — scoped destructive actions for this account. */}
              <div
                className="mt-2 rounded-lg border border-red-500/25 bg-red-500/[0.04] p-3 space-y-2.5"
                data-testid="user-danger-zone"
              >
                <div className="flex items-start gap-2.5">
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-red-600 dark:text-red-400">Danger Zone</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Scoped, permanent deletes for this account. Clearing jobs removes only job records;
                      clearing customers removes only customer records. Nothing else is touched.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pl-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setClearJobsDialogOpen(true)}
                    className="border-red-500/40 text-red-600 hover:text-red-700 hover:bg-red-500/10 shrink-0"
                    data-testid="clear-jobs-data-btn"
                  >
                    <Eraser className="w-3.5 h-3.5 mr-1.5" />
                    Clear Jobs Data
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setClearCustomersDialogOpen(true)}
                    className="border-red-500/40 text-red-600 hover:text-red-700 hover:bg-red-500/10 shrink-0"
                    data-testid="clear-customers-data-btn"
                  >
                    <Eraser className="w-3.5 h-3.5 mr-1.5" />
                    Clear Customers Data
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Repair Tech Commissions — preview + confirm dialog */}
      <Dialog open={repairDialogOpen} onOpenChange={(v) => !repairApplying && setRepairDialogOpen(v)}>
        <DialogContent className="bg-background border-theme text-foreground max-w-lg max-h-[85vh] overflow-y-auto" data-testid="repair-commissions-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
              <Wrench className="w-5 h-5 text-cyan-500" />
              Repair Tech Commissions
            </DialogTitle>
          </DialogHeader>
          {repairLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Scanning jobs…</span>
            </div>
          ) : repairPreview ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-muted/40 p-3">
                <p className="text-xs text-muted-foreground">Account</p>
                <p className="text-sm font-semibold text-foreground">{repairPreview.account?.name || selectedUser?.name}</p>
                <p className="text-xs text-muted-foreground">{repairPreview.account?.email || ""}</p>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-muted/40 p-2">
                  <p className="text-lg font-bold text-foreground" data-testid="repair-scanned-count">{repairPreview.total_jobs_scanned}</p>
                  <p className="text-[10px] text-muted-foreground uppercase">Scanned</p>
                </div>
                <div className="rounded-lg bg-cyan-500/10 p-2">
                  <p className="text-lg font-bold text-cyan-600" data-testid="repair-update-count">{repairPreview.jobs_to_update}</p>
                  <p className="text-[10px] text-muted-foreground uppercase">Will update</p>
                </div>
                <div className="rounded-lg bg-emerald-500/10 p-2">
                  <p className="text-lg font-bold text-emerald-600">{repairPreview.already_correct}</p>
                  <p className="text-[10px] text-muted-foreground uppercase">Already correct</p>
                </div>
              </div>
              {repairPreview.jobs_to_update > 0 && (
                <>
                  <div className="rounded-lg border border-border overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left px-3 py-1.5 text-muted-foreground font-medium">Technician</th>
                          <th className="text-right px-3 py-1.5 text-muted-foreground font-medium">Jobs</th>
                          <th className="text-right px-3 py-1.5 text-muted-foreground font-medium">New rate</th>
                          <th className="text-right px-3 py-1.5 text-muted-foreground font-medium">Commission Δ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(repairPreview.per_technician || []).map((t) => (
                          <tr key={t.technician_id} className="border-t border-border" data-testid={`repair-tech-row-${t.technician_id}`}>
                            <td className="px-3 py-1.5 text-foreground">{t.name}</td>
                            <td className="px-3 py-1.5 text-right">{t.jobs_to_update}</td>
                            <td className="px-3 py-1.5 text-right font-medium">{t.new_rate}</td>
                            <td className="px-3 py-1.5 text-right text-muted-foreground">
                              ${t.commission_before.toFixed(2)} → <span className="text-cyan-600 font-medium">${t.commission_after.toFixed(2)}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Completed-job commissions: <span className="font-medium">${(repairPreview.completed_commission_before || 0).toFixed(2)}</span> →{" "}
                    <span className="font-medium text-cyan-600">${(repairPreview.completed_commission_after || 0).toFixed(2)}</span>.
                    Includes settled jobs. Custom multi-tech splits are kept — only the base rate is rewritten.
                  </p>
                </>
              )}
              {(repairPreview.techs_without_config || []).length > 0 && (
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-2.5">
                  <p className="text-[11px] text-amber-600 dark:text-amber-400">
                    Skipped (no commission configured on the technician profile):{" "}
                    {repairPreview.techs_without_config.map((t) => `${t.name} (${t.jobs} jobs)`).join(", ")}
                  </p>
                </div>
              )}
              {repairPreview.jobs_to_update === 0 && (
                <p className="text-sm text-emerald-600 text-center py-2" data-testid="repair-nothing-to-do">
                  All jobs already match the technicians' commission config — nothing to repair.
                </p>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRepairDialogOpen(false)} disabled={repairApplying} data-testid="repair-cancel-btn">
              Close
            </Button>
            {repairPreview?.jobs_to_update > 0 && (
              <Button
                onClick={applyRepair}
                disabled={repairApplying}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="repair-apply-btn"
              >
                {repairApplying ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Repairing…</>
                ) : (
                  <><Wrench className="w-4 h-4 mr-1.5" /> Repair {repairPreview.jobs_to_update} Job{repairPreview.jobs_to_update === 1 ? "" : "s"}</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Repair Job Data — run + progress + result dialog */}
      <Dialog open={repairDataDialogOpen} onOpenChange={(v) => !repairDataRunning && setRepairDataDialogOpen(v)}>
        <DialogContent className="bg-background border-theme text-foreground max-w-md" data-testid="repair-job-data-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
              <Database className="w-5 h-5 text-cyan-500" />
              Repair Data
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-muted/40 p-3">
              <p className="text-xs text-muted-foreground">Account</p>
              <p className="text-sm font-semibold text-foreground">{selectedUser?.name}</p>
              <p className="text-xs text-muted-foreground">{selectedUser?.email || ""}</p>
            </div>
            {!repairDataRunning && !repairDataResult && (
              <p className="text-sm text-muted-foreground">
                Scans every job in this account. Dates (<span className="font-mono text-xs">scheduled_date</span>,{" "}
                <span className="font-mono text-xs">created_at</span>, <span className="font-mono text-xs">completed_at</span>)
                that aren't valid ISO get normalized or cleared, and <span className="font-mono text-xs">price</span> /{" "}
                <span className="font-mono text-xs">duration_hours</span> are forced to numbers (0 when missing or invalid).
                Clean records are left untouched.
              </p>
            )}
            {(repairDataRunning || repairDataResult) && (
              <div className="space-y-2" data-testid="repair-data-progress-block">
                <Progress
                  value={repairDataProgress?.total ? Math.min(100, (repairDataProgress.processed / repairDataProgress.total) * 100) : (repairDataRunning ? 8 : 100)}
                  className="h-2"
                />
                <p className="text-[11px] text-muted-foreground text-center tabular-nums" data-testid="repair-data-progress-text">
                  {repairDataRunning
                    ? `Cleaning… ${repairDataProgress?.processed ?? 0}${repairDataProgress?.total ? ` / ${repairDataProgress.total}` : ""} jobs`
                    : `Done — ${repairDataResult?.scanned ?? 0} jobs scanned`}
                </p>
              </div>
            )}
            {repairDataResult && (
              <div className="space-y-3" data-testid="repair-data-result">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-muted/40 p-2">
                    <p className="text-lg font-bold text-foreground" data-testid="repair-data-scanned">{repairDataResult.scanned}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Scanned</p>
                  </div>
                  <div className="rounded-lg bg-cyan-500/10 p-2">
                    <p className="text-lg font-bold text-cyan-600" data-testid="repair-data-updated">{repairDataResult.jobs_updated}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Cleaned</p>
                  </div>
                  <div className="rounded-lg bg-emerald-500/10 p-2">
                    <p className="text-lg font-bold text-emerald-600">{repairDataResult.already_clean}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Already clean</p>
                  </div>
                </div>
                {repairDataResult.jobs_updated > 0 && (
                  <div className="rounded-lg border border-border overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left px-3 py-1.5 text-muted-foreground font-medium">Field</th>
                          <th className="text-right px-3 py-1.5 text-muted-foreground font-medium">Values fixed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(repairDataResult.fixes || {}).filter(([, n]) => n > 0).map(([field, n]) => (
                          <tr key={field} className="border-t border-border">
                            <td className="px-3 py-1.5 font-mono text-foreground">{field}</td>
                            <td className="px-3 py-1.5 text-right text-cyan-600 font-medium">{n}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="text-sm text-emerald-600 text-center flex items-center justify-center gap-1.5" data-testid="repair-data-success-msg">
                  <CheckCircle className="w-4 h-4" />
                  {repairDataResult.jobs_updated > 0
                    ? `All records cleaned — ${repairDataResult.jobs_updated} job${repairDataResult.jobs_updated === 1 ? "" : "s"} repaired.`
                    : "All records are clean — nothing needed fixing."}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRepairDataDialogOpen(false)} disabled={repairDataRunning} data-testid="repair-data-close-btn">
              Close
            </Button>
            {!repairDataResult && (
              <Button
                onClick={runRepairJobData}
                disabled={repairDataRunning}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="repair-data-run-btn"
              >
                {repairDataRunning ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Cleaning…</>
                ) : (
                  <><Database className="w-4 h-4 mr-1.5" /> Run Repair</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear Jobs Data — confirmation dialog */}
      <Dialog open={clearJobsDialogOpen} onOpenChange={(v) => !clearingJobs && setClearJobsDialogOpen(v)}>
        <DialogContent className="bg-background border-theme text-foreground max-w-md" data-testid="clear-jobs-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Clear Jobs Data
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              You are about to permanently delete <span className="font-semibold text-foreground" data-testid="clear-jobs-target-job-count">
                {userDetails?.stats?.total_jobs ?? 0}
              </span>{" "}
              job record{(userDetails?.stats?.total_jobs ?? 0) === 1 ? "" : "s"} for:
            </p>
            <div className="rounded-lg border border-border bg-muted/40 p-3" data-testid="clear-jobs-target-account">
              <p className="text-xs text-muted-foreground">Account</p>
              <p className="text-sm font-semibold text-foreground">{selectedUser?.name || "—"}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{selectedUser?.email || ""}</p>
              {selectedUser?.company_name && (
                <p className="text-xs text-muted-foreground">{selectedUser.company_name}</p>
              )}
            </div>
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 flex items-start gap-2.5">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <p className="text-xs text-red-600 dark:text-red-400">
                <span className="font-semibold">This cannot be undone.</span> All jobs belonging to this account
                will be permanently removed. Customers, invoices, team members, and settings will not be affected.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setClearJobsDialogOpen(false)}
              disabled={clearingJobs}
              data-testid="clear-jobs-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={clearJobsForSelectedUser}
              disabled={clearingJobs}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="clear-jobs-confirm-btn"
            >
              {clearingJobs ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Clearing…</>
              ) : (
                <><Eraser className="w-4 h-4 mr-1.5" /> Clear Jobs Data</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear Customers Data — confirmation dialog */}
      <Dialog open={clearCustomersDialogOpen} onOpenChange={(v) => !clearingCustomers && setClearCustomersDialogOpen(v)}>
        <DialogContent className="bg-background border-theme text-foreground max-w-md" data-testid="clear-customers-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Clear Customers Data
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              You are about to permanently delete <span className="font-semibold text-foreground" data-testid="clear-customers-target-count">
                {userDetails?.stats?.total_customers ?? 0}
              </span>{" "}
              customer record{(userDetails?.stats?.total_customers ?? 0) === 1 ? "" : "s"} for:
            </p>
            <div className="rounded-lg border border-border bg-muted/40 p-3" data-testid="clear-customers-target-account">
              <p className="text-xs text-muted-foreground">Account</p>
              <p className="text-sm font-semibold text-foreground">{selectedUser?.name || "—"}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{selectedUser?.email || ""}</p>
              {selectedUser?.company_name && (
                <p className="text-xs text-muted-foreground">{selectedUser.company_name}</p>
              )}
            </div>
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 flex items-start gap-2.5">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <p className="text-xs text-red-600 dark:text-red-400">
                <span className="font-semibold">This cannot be undone.</span> All customers belonging to this
                account will be permanently removed. Jobs, invoices, team members, and settings will not be
                affected — jobs will keep their stored customer name but lose the customer link.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setClearCustomersDialogOpen(false)}
              disabled={clearingCustomers}
              data-testid="clear-customers-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={clearCustomersForSelectedUser}
              disabled={clearingCustomers}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="clear-customers-confirm-btn"
            >
              {clearingCustomers ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Clearing…</>
              ) : (
                <><Eraser className="w-4 h-4 mr-1.5" /> Clear Customers Data</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-background border-theme text-foreground max-w-xl max-h-[90vh] overflow-y-auto" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground font-['Manrope']">Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-muted-foreground text-xs font-medium">Name</label>
              <Input value={editData.name || ""} onChange={(e) => setEditData({ ...editData, name: e.target.value })} className="bg-theme-surface border-theme text-foreground mt-1 h-9" />
            </div>
            <div>
              <label className="text-muted-foreground text-xs font-medium">Email</label>
              <Input value={editData.email || ""} onChange={(e) => setEditData({ ...editData, email: e.target.value })} className="bg-theme-surface border-theme text-foreground mt-1 h-9" />
            </div>
            <div>
              <label className="text-muted-foreground text-xs font-medium">Company Name</label>
              <Input value={editData.company_name || ""} onChange={(e) => setEditData({ ...editData, company_name: e.target.value })} className="bg-theme-surface border-theme text-foreground mt-1 h-9" />
            </div>
            <div>
              <label className="text-muted-foreground text-xs font-medium">Subscription Plan</label>
              <Select value={editData.subscription_plan} onValueChange={(v) => setEditData({ ...editData, subscription_plan: v })}>
                <SelectTrigger className="bg-theme-surface border-theme text-foreground mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-theme">
                  {subscriptionPlans.map(plan => (
                    <SelectItem key={plan.plan_id} value={plan.plan_id}>{plan.name} (${plan.price}/mo)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-muted-foreground text-xs font-medium">Status</label>
              <Select value={editData.subscription_status} onValueChange={(v) => setEditData({ ...editData, subscription_status: v })}>
                <SelectTrigger className="bg-theme-surface border-theme text-foreground mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-theme">
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="trialing">Trialing</SelectItem>
                  <SelectItem value="frozen">Frozen</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-muted-foreground text-xs font-medium">Account Type</label>
              <Select value={editData.account_type || "standard"} onValueChange={(v) => setEditData({ ...editData, account_type: v === "standard" ? null : v })}>
                <SelectTrigger className="bg-theme-surface border-theme text-foreground mt-1 h-9" data-testid="edit-user-account-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-theme">
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="multi_location_store">Multi-Location Store</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={editData.is_admin || false} onChange={(e) => setEditData({ ...editData, is_admin: e.target.checked })} className="rounded" />
              <label className="text-foreground text-sm">Admin Access</label>
            </div>
            <div className="flex items-center gap-2" data-testid="internal-account-row">
              <input type="checkbox" checked={editData.is_internal || false} onChange={(e) => setEditData({ ...editData, is_internal: e.target.checked })} className="rounded" data-testid="internal-account-check" />
              <label className="text-foreground text-sm">Internal / test account
                <span className="block text-[11px] text-muted-foreground">Excluded from revenue, MRR &amp; conversion metrics</span>
              </label>
            </div>

            {/* ── Custom Limits (per-user override layered on top of plan) ── */}
            <CustomLimitsSection
              plan={subscriptionPlans.find(p => p.plan_id === editData.subscription_plan)}
              overrides={editData.plan_overrides || {}}
              setOverrides={(next) => setEditData({ ...editData, plan_overrides: next })}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-theme text-foreground h-9">Cancel</Button>
            <Button onClick={saveUser} className="bg-[#0046BE] text-white hover:bg-[#0046BE]/80 h-9">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-background border-theme text-foreground" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground font-['Manrope']">Create New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-muted-foreground text-xs font-medium">Full Name *</label>
              <Input value={createData.name} onChange={(e) => setCreateData({ ...createData, name: e.target.value })} placeholder="John Doe" className="bg-theme-surface border-theme text-foreground mt-1 h-9" data-testid="create-user-name" />
            </div>
            <div>
              <label className="text-muted-foreground text-xs font-medium">Email *</label>
              <Input type="email" value={createData.email} onChange={(e) => setCreateData({ ...createData, email: e.target.value })} placeholder="john@example.com" className="bg-theme-surface border-theme text-foreground mt-1 h-9" data-testid="create-user-email" />
            </div>
            <div>
              <label className="text-muted-foreground text-xs font-medium">Password *</label>
              <Input type="password" value={createData.password} onChange={(e) => setCreateData({ ...createData, password: e.target.value })} placeholder="Min. 6 characters" className="bg-theme-surface border-theme text-foreground mt-1 h-9" data-testid="create-user-password" />
            </div>
            <div>
              <label className="text-muted-foreground text-xs font-medium">Company Name</label>
              <Input value={createData.company_name} onChange={(e) => setCreateData({ ...createData, company_name: e.target.value })} placeholder="Acme Plumbing" className="bg-theme-surface border-theme text-foreground mt-1 h-9" data-testid="create-user-company" />
            </div>
            <div>
              <label className="text-muted-foreground text-xs font-medium">Subscription Plan</label>
              <Select value={createData.subscription_plan} onValueChange={(v) => setCreateData({ ...createData, subscription_plan: v })}>
                <SelectTrigger className="bg-theme-surface border-theme text-foreground mt-1 h-9" data-testid="create-user-plan"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-theme">
                  {subscriptionPlans.map(plan => (
                    <SelectItem key={plan.plan_id} value={plan.plan_id}>{plan.name} (${plan.price}/mo)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-muted-foreground text-xs font-medium">Status</label>
              <Select value={createData.subscription_status} onValueChange={(v) => setCreateData({ ...createData, subscription_status: v })}>
                <SelectTrigger className="bg-theme-surface border-theme text-foreground mt-1 h-9" data-testid="create-user-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-theme">
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="trialing">Trialing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={createData.is_admin} onChange={(e) => setCreateData({ ...createData, is_admin: e.target.checked })} className="rounded" data-testid="create-user-admin-check" />
              <label className="text-foreground text-sm">Admin Access</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} className="border-theme text-foreground h-9">Cancel</Button>
            <Button onClick={createUser} disabled={creating} className="bg-[#0046BE] text-white hover:bg-[#0046BE]/80 h-9" data-testid="create-user-submit">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MergeAccountsDialog
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        sourceUser={mergeSource}
        users={users}
        getAuthConfig={getAuthConfig}
        onMerged={fetchUsers}
      />
    </div>
  );
};

export default AdminUsers;
